import React, { useState, useEffect } from 'react';
import { X  } from 'lucide-react';

const STORAGE_KEY = 'announcementBannerDismissedDate';

/**
 * @returns {boolean} - true if banner should be shown
 */
function shouldShowBanner() {
  try {
    const dismissedDateStr = localStorage.getItem(STORAGE_KEY);
    
    if (!dismissedDateStr) {
      return true;
    }
    
    const dismissedDate = new Date(dismissedDateStr);
    const today = new Date();

    dismissedDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    return today > dismissedDate;
  } catch (error) {
    console.error('Error checking announcement banner state:', error);
    return true;
  }
}

function storeDismissalDate() {
  try {
    const today = new Date().toISOString();
    localStorage.setItem(STORAGE_KEY, today);
  } catch (error) {
    console.error('Error storing announcement banner dismissal:', error);
  }
}

/**
 * @param {string} name - The cookie name
 * @returns {string|null} - The cookie value or null
 */
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(';').shift();
  }
  return null;
}

/**
 * @param {string} baseUrl
 * @returns {string} URL with HubSpot tracking params appended
 */
function buildHubSpotUrl(baseUrl) {
  const params = new URLSearchParams();
  
  const hstc = getCookie('__hstc');
  const hssc = getCookie('__hssc');
  const hsfp = getCookie('__hsfp');
  
  if (hstc) params.append('__hstc', hstc);
  if (hssc) params.append('__hssc', hssc);
  if (hsfp) params.append('__hsfp', hsfp);
  const queryString = params.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

export function AnnouncementBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [gotvUrl, setGotvUrl] = useState('https://gotv.com');
  
  useEffect(() => {
    setIsVisible(shouldShowBanner());
    setGotvUrl(buildHubSpotUrl('https://gotv.com'));
  }, []);
  
  const handleDismiss = () => {
    storeDismissalDate();
    setIsVisible(false);
  };
  
  if (!isVisible) {
    return null;
  }
  
  return (
    <div 
      className="notification-bar relative z-[9998] w-full bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700"
    >
      <div className="h-full flex items-center justify-center px-8 sm:px-4">
        <p>
          AdvocacyLab is now part of{' '}
          <a 
            href={gotvUrl} 
            target="_blank" 
            rel="noopener noreferrer"
          >
            FullPAC
          </a>
          . Click here to Learn More &amp; Invest.{' '}
          <a 
            href={gotvUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="whitespace-nowrap"
          >
            Visit GOTV.com
          </a>
        </p>
        
        <button
          onClick={handleDismiss}
          className="notification-bar-dismiss absolute right-2 sm:right-4 p-1 hover:opacity-70 transition-opacity"
          aria-label="Dismiss announcement"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      
      <style>{`
        .notification-bar {
          min-height: 30px;
          padding-top: 6px;
          padding-bottom: 6px;
          text-align: center;
          font-size: 14px;
          font-family: "DM Sans", sans-serif;
          color: #6e6d79;
        }
        @media (min-width: 640px) {
          .notification-bar {
            height: 30px;
            padding-top: 0;
            padding-bottom: 0;
            line-height: 30px;
          }
        }
        .notification-bar p {
          margin: 0;
          line-height: 1.4;
        }
        @media (min-width: 640px) {
          .notification-bar p {
            line-height: 30px;
          }
        }
        .notification-bar a {
          color: #f1592a;
          font-weight: 700;
          text-decoration: underline;
        }
        .notification-bar a:hover {
          opacity: 0.8;
        }
        .notification-bar-dismiss {
          color: #6e6d79;
        }
        
        /* Dark mode */
        .dark .notification-bar {
          color: #e4e4e7;
        }
        .dark .notification-bar a {
          color: #fb923c;
        }
        .dark .notification-bar-dismiss {
          color: #a1a1aa;
        }
      `}</style>
    </div>
  );
}
